import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import api from "../api/axios";

const PAGE_NAME = "Maintenance";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const toDateTimeInput = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
};

const initialMaintenance = {
  enabled: false,
  title: "We will be back shortly",
  message:
    "We are making a few thoughtful improvements. Your saved cart and account details are safe.",
  estimatedEndAt: "",
  updatedAt: null,
  canEdit: false,
};

const Maintenance = () => {
  const [maintenance, setMaintenance] = useState(initialMaintenance);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const loadMaintenance = useCallback(async () => {
    try {
      setLoading(true);
      setAccessDenied(false);

      const response = await api.get("/admin/maintenance", {
        headers: getPermissionHeaders("view"),
      });
      const data = response.data?.data || {};

      setMaintenance({
        enabled: Boolean(data.enabled),
        title: data.title || initialMaintenance.title,
        message: data.message || initialMaintenance.message,
        estimatedEndAt: toDateTimeInput(data.estimatedEndAt),
        updatedAt: data.updatedAt || null,
        canEdit: Boolean(data.canEdit),
      });
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
        return;
      }

      toast.error(
        error.response?.data?.message ||
          "Unable to load storefront maintenance settings."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMaintenance();
  }, [loadMaintenance]);

  const updateField = (field, value) => {
    if (!maintenance.canEdit) return;

    setMaintenance((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const saveMaintenance = async () => {
    if (!maintenance.canEdit) {
      toast.error("You do not have permission to edit maintenance mode.");
      return;
    }

    const title = maintenance.title.trim();
    const message = maintenance.message.trim();
    let estimatedEndAt = null;

    if (!title || !message) {
      toast.error("Add both a maintenance title and customer message.");
      return;
    }

    if (maintenance.estimatedEndAt) {
      const parsedEndAt = new Date(maintenance.estimatedEndAt);

      if (Number.isNaN(parsedEndAt.getTime())) {
        toast.error("Choose a valid estimated return time.");
        return;
      }

      estimatedEndAt = parsedEndAt.toISOString();
    }

    try {
      setSaving(true);

      const response = await api.put(
        "/admin/maintenance",
        {
          enabled: maintenance.enabled,
          title,
          message,
          estimatedEndAt,
        },
        {
          headers: getPermissionHeaders("edit"),
        }
      );
      const data = response.data?.data || {};

      setMaintenance((previous) => ({
        enabled: Boolean(data.enabled),
        title: data.title || title,
        message: data.message || message,
        estimatedEndAt: toDateTimeInput(data.estimatedEndAt),
        updatedAt: data.updatedAt || null,
        canEdit: previous.canEdit,
      }));

      toast.success(response.data?.message || "Maintenance settings saved.");
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error(
          error.response?.data?.message ||
            "Unable to save maintenance settings."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-page">
      <div className="page-header">
        <div>
          <p>System controls</p>
          <h1>Maintenance</h1>
          <span>Manage ecommerce storefront availability during updates.</span>
        </div>

        {!loading && !accessDenied && (
          <span
            className={`maintenance-status-badge ${
              maintenance.enabled ? "active" : "inactive"
            }`}
          >
            <i
              className={`pi ${
                maintenance.enabled ? "pi-lock" : "pi-check-circle"
              }`}
              aria-hidden="true"
            />
            {maintenance.enabled ? "Maintenance active" : "Storefront live"}
          </span>
        )}
      </div>

      <div className="page-card maintenance-settings-card">
        {loading ? (
          <div className="maintenance-settings-loading" role="status">
            <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            Loading storefront status...
          </div>
        ) : accessDenied ? (
          <div className="maintenance-access-denied">
            <i className="pi pi-lock" aria-hidden="true" />
            <h2>Maintenance permission required</h2>
            <p>
              Your role needs the Maintenance view permission to access this
              page.
            </p>
          </div>
        ) : (
          <>
            <div className="maintenance-settings-heading">
              <div>
                <p className="settings-section-label">
                  Storefront availability
                </p>
                <h2>Maintenance mode</h2>
                <p>
                  Pause the ecommerce storefront before backend work or a
                  deploy. This setting is stored in MySQL and does not change
                  during a build.
                </p>
              </div>

              {!maintenance.canEdit && (
                <span className="maintenance-read-only-badge">
                  <i className="pi pi-eye" aria-hidden="true" />
                  View only
                </span>
              )}
            </div>

            <div className="maintenance-settings-form">
              <div className="maintenance-toggle-row">
                <div>
                  <strong>Pause customer access</strong>
                  <p>
                    New storefront API activity will receive a maintenance
                    response. Admin access, webhooks, and payment verification
                    remain available.
                  </p>
                </div>

                <label className="theme-switch">
                  <input
                    type="checkbox"
                    checked={maintenance.enabled}
                    disabled={!maintenance.canEdit}
                    onChange={(event) =>
                      updateField("enabled", event.target.checked)
                    }
                    aria-label="Enable storefront maintenance mode"
                  />
                  <span className="slider" />
                </label>
              </div>

              <div className="maintenance-field-grid">
                <label className="maintenance-field">
                  <span>Customer-facing title</span>
                  <input
                    type="text"
                    value={maintenance.title}
                    maxLength={120}
                    disabled={!maintenance.canEdit}
                    onChange={(event) => updateField("title", event.target.value)}
                    placeholder="We will be back shortly"
                  />
                </label>

                <label className="maintenance-field">
                  <span>Estimated return (optional)</span>
                  <input
                    type="datetime-local"
                    value={maintenance.estimatedEndAt}
                    disabled={!maintenance.canEdit}
                    onChange={(event) =>
                      updateField("estimatedEndAt", event.target.value)
                    }
                  />
                </label>

                <label className="maintenance-field maintenance-message-field">
                  <span>Customer-facing message</span>
                  <textarea
                    rows="4"
                    value={maintenance.message}
                    maxLength={600}
                    disabled={!maintenance.canEdit}
                    onChange={(event) =>
                      updateField("message", event.target.value)
                    }
                  />
                  <small>{maintenance.message.length}/600 characters</small>
                </label>
              </div>

              <div className="maintenance-settings-actions">
                <p>
                  {maintenance.updatedAt
                    ? `Last saved ${new Date(
                        maintenance.updatedAt
                      ).toLocaleString("en-IN")}`
                    : "This setting has not been changed yet."}
                </p>

                {maintenance.canEdit && (
                  <button
                    type="button"
                    className="primary-action"
                    onClick={saveMaintenance}
                    disabled={saving}
                  >
                    <i
                      className={`pi ${
                        saving ? "pi-spin pi-spinner" : "pi-save"
                      }`}
                      aria-hidden="true"
                    />
                    {saving ? "Saving..." : "Save storefront status"}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Maintenance;
